/**
 * Crew member access — isolation + computer-action rules for Crew chats
 *
 * A Crew chat carries a validated `crewMemberId` (catalog MateId, e.g.
 * petal / jovi / star). That id:
 * - scopes the cloud browser to its own session + Chrome profile
 *   (getCanvasBrowserPrincipalId → separate DynamoDB session + EFS profile)
 * - scopes Private vault logins saved in that chat to the member
 *   (losi_user_credentials.crew_member_id; shared rows stay null)
 * - routes computer actions through the Crew "Computer" rule
 *   (Ask → plan approval, Allow → run, Never → blocked)
 *
 * Security invariant: Crew mode can only NARROW what normal Losi chat can
 * do. A forged or unknown id normalizes to null (= plain Losi chat).
 *
 * Isomorphic (no server-only imports) — used by chat client, tool runtime,
 * and API routes.
 */

import {
  CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS,
  CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS
} from '@/lib/crew/computer-plan-gate-prep'
import {
  CREW_CUSTOM_RULE_MODE_ALLOW,
  CREW_CUSTOM_RULE_MODE_ASK,
  CREW_CUSTOM_RULE_MODE_NEVER,
  type CrewCustomRuleMode
} from '@/lib/crew/custom-rules'
import {
  buildCrewMemberMandateSystemNote,
  resolveDesignatedTemplateForMate
} from '@/lib/crew/member-designation'
import { MATE_BY_ID, resolveMateId, type MateId } from '@/lib/crew/mates'

// --------------

export type CrewMemberId = MateId

/** Request header carrying the active Crew member for browser API routes. */
export const CREW_MEMBER_HEADER = 'x-losi-crew-member-id' as const

/** Chat request body field. */
export const CREW_MEMBER_BODY_FIELD = 'crewMemberId' as const

const PRINCIPAL_SEPARATOR = '::crew::'

// --------------

/** Validate any raw id (catalog or engine shape) → catalog MateId or null. */
export function normalizeCrewMemberId(raw: unknown): CrewMemberId | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed || trimmed.length > 32) return null
  return resolveMateId(trimmed)
}

/** Display label for a member (Peta / Jovi / Star / …). */
export function crewMemberLabel(crewMemberId: CrewMemberId): string {
  return MATE_BY_ID.get(crewMemberId)?.label ?? crewMemberId
}

// --------------

export type CrewComputerPrincipalMode = 'shared' | 'isolated'

/**
 * Browser principal. Plain Losi = userId (unchanged).
 * - isolated (default for single-mate): each mate gets its own DynamoDB session
 *   + EFS Chrome profile (cookies / logins never shared across members).
 * - shared (Crew Chat / Grok-style): whole Crew uses the user principal so
 *   mates hand off on one computer. Crew Ask/Allow/Never rules still apply.
 */
export function getCanvasBrowserPrincipalId(
  userId: string,
  crewMemberId?: string | null,
  options?: { computerMode?: CrewComputerPrincipalMode | null }
): string {
  if (options?.computerMode === 'shared') return userId
  const member = normalizeCrewMemberId(crewMemberId)
  return member ? `${userId}${PRINCIPAL_SEPARATOR}${member}` : userId
}

/** Recover the owning user (for server events) and member from a principal. */
export function parseCanvasBrowserPrincipalId(principalId: string): {
  crewMemberId: CrewMemberId | null
  userId: string
} {
  const index = principalId.indexOf(PRINCIPAL_SEPARATOR)
  if (index < 0) return { crewMemberId: null, userId: principalId }
  return {
    crewMemberId: normalizeCrewMemberId(
      principalId.slice(index + PRINCIPAL_SEPARATOR.length)
    ),
    userId: principalId.slice(0, index)
  }
}

// --------------

/**
 * Vault key for a member-scoped login. Keeps the owner+key unique index
 * from colliding with the user's own login for the same domain.
 */
export function crewScopedVaultCredentialKey(
  baseKey: string,
  crewMemberId?: string | null
): string {
  const member = normalizeCrewMemberId(crewMemberId)
  return member ? `${baseKey}_CREW_${member.toUpperCase()}` : baseKey
}

/**
 * Can a caller in `contextMemberId` use a credential owned by
 * `credentialMemberId`? Shared (null) rows are usable everywhere; member rows
 * only by that member — never by plain Losi or another member.
 */
export function crewMemberCanUseCredential(
  credentialMemberId: string | null | undefined,
  contextMemberId: string | null | undefined
): boolean {
  const owner = normalizeCrewMemberId(credentialMemberId)
  if (!owner) return true
  return owner === normalizeCrewMemberId(contextMemberId)
}

// --------------

const COMPUTER_TOOL_NAMES = [
  ...CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS,
  ...CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS
] as const

/** Bare function ids (after `toolName__`) — prefix-agnostic matching. */
const COMPUTER_TOOL_BARE_IDS = new Set<string>(
  COMPUTER_TOOL_NAMES.map((name) => name.split('__').pop() || name)
)

function bareToolId(toolName: string): string {
  return toolName.includes('__') ? toolName.split('__').pop() || toolName : toolName
}

/**
 * Computer actions = cloud computer (sandbox), cloud browser mutates,
 * desktop control and desktop filesystem writes. Matches by bare function
 * id so a different `prefix__` spelling cannot slip past the rule.
 */
export function isCrewComputerActionTool(toolName: unknown): boolean {
  if (typeof toolName !== 'string' || !toolName.trim()) return false
  return COMPUTER_TOOL_BARE_IDS.has(bareToolId(toolName.trim()))
}

// --------------

export const CREW_COMPUTER_NEVER_BLOCKED_MESSAGE =
  'Blocked by your Crew rules: computer actions are set to Never for Crew. Tell the user, and offer to do it in a normal Losi chat or change the rule in Your crew → Rules.' as const

export const CREW_COMPUTER_ASK_BLOCKED_MESSAGE =
  'Blocked until approved: your Crew rules ask before computer actions. Call propose_plan with the computer steps and STOP; the user approves in the plan card, then you can continue.' as const

export type CrewComputerToolDecision =
  | { allowed: true }
  | { allowed: false; message: string }

/**
 * Decide whether a tool call may run in a Crew chat.
 * - Not a Crew chat, or not a computer tool → allowed (normal gates apply).
 * - Never → blocked.
 * - Ask → allowed only once the chat plan is approved (status `active`).
 * - Allow → allowed.
 */
export function resolveCrewComputerToolDecision(input: {
  crewMemberId: string | null | undefined
  mode: CrewCustomRuleMode | null | undefined
  planStatus: unknown
  toolName: unknown
}): CrewComputerToolDecision {
  if (!normalizeCrewMemberId(input.crewMemberId)) return { allowed: true }
  if (!isCrewComputerActionTool(input.toolName)) return { allowed: true }

  const mode = input.mode ?? CREW_CUSTOM_RULE_MODE_ASK

  if (mode === CREW_CUSTOM_RULE_MODE_NEVER) {
    return { allowed: false, message: CREW_COMPUTER_NEVER_BLOCKED_MESSAGE }
  }

  if (mode === CREW_CUSTOM_RULE_MODE_ALLOW) {
    return { allowed: true }
  }

  return input.planStatus === 'active'
    ? { allowed: true }
    : { allowed: false, message: CREW_COMPUTER_ASK_BLOCKED_MESSAGE }
}

/** Drop computer tools the Crew rule would block this turn. */
export function filterToolsForCrewComputerRule<
  T extends { function?: { name?: string }; name?: string }
>(
  tools: T[],
  input: {
    crewMemberId: string | null | undefined
    mode: CrewCustomRuleMode | null | undefined
    planStatus: unknown
  }
): T[] {
  if (!normalizeCrewMemberId(input.crewMemberId)) return tools
  return tools.filter(
    (tool) =>
      resolveCrewComputerToolDecision({
        ...input,
        toolName: tool.function?.name || tool.name
      }).allowed
  )
}

// --------------

/**
 * Trusted crew context attached to the tool user object by tools-stream
 * (same channel as desktop_device_id). Never read from model arguments.
 */
export type CrewToolUserContext = {
  crew_computer_mode: CrewComputerPrincipalMode
  crew_computer_rule: CrewCustomRuleMode
  crew_member_id: CrewMemberId
  crew_plan_status: string | null
}

export function buildCrewToolUserContext(input: {
  crewMemberId: CrewMemberId
  computerMode?: CrewComputerPrincipalMode | null
  mode: CrewCustomRuleMode | null | undefined
  planStatus: string | null | undefined
}): CrewToolUserContext {
  return {
    crew_computer_mode:
      input.computerMode === 'shared' ? 'shared' : 'isolated',
    crew_computer_rule: input.mode ?? CREW_CUSTOM_RULE_MODE_ASK,
    crew_member_id: input.crewMemberId,
    crew_plan_status: input.planStatus ?? null
  }
}

export function readCrewToolUserContext(user: unknown): CrewToolUserContext | null {
  if (!user || typeof user !== 'object') return null
  const record = user as Record<string, unknown>
  const crewMemberId = normalizeCrewMemberId(record.crew_member_id)
  if (!crewMemberId) return null
  const rule = record.crew_computer_rule
  return {
    crew_computer_mode:
      record.crew_computer_mode === 'shared' ? 'shared' : 'isolated',
    crew_computer_rule:
      rule === CREW_CUSTOM_RULE_MODE_ALLOW || rule === CREW_CUSTOM_RULE_MODE_NEVER
        ? rule
        : CREW_CUSTOM_RULE_MODE_ASK,
    crew_member_id: crewMemberId,
    crew_plan_status:
      typeof record.crew_plan_status === 'string' ? record.crew_plan_status : null
  }
}

// --------------

/**
 * Write the trusted Crew member id onto tool arguments (top-level + nested
 * `parameters`). When there is no trusted member, any model-supplied value is
 * deleted so a tool call can never claim another member's browser or vault.
 */
export function applyTrustedCrewMemberId(
  args: Record<string, unknown> | null | undefined,
  nested: Record<string, unknown> | null | undefined,
  crewMemberId: CrewMemberId | null,
  computerMode?: CrewComputerPrincipalMode | null
): void {
  for (const target of [args, nested]) {
    if (!target || typeof target !== 'object') continue
    if (crewMemberId) {
      target.crewMemberId = crewMemberId
      target.crew_member_id = crewMemberId
      const mode = computerMode === 'shared' ? 'shared' : 'isolated'
      target.crewComputerMode = mode
      target.crew_computer_mode = mode
    } else {
      delete target.crewMemberId
      delete target.crew_member_id
      delete target.crewComputerMode
      delete target.crew_computer_mode
    }
  }
}

// --------------

/** Model-facing note for Crew chats (identity, computer mode, active rule). */
export function buildCrewMemberSystemNote(input: {
  crewMemberId: CrewMemberId
  mode: CrewCustomRuleMode
  computerMode?: CrewComputerPrincipalMode | null
}): string {
  const label = crewMemberLabel(input.crewMemberId)
  const shared = input.computerMode === 'shared'
  const rule =
    input.mode === CREW_CUSTOM_RULE_MODE_NEVER
      ? 'Computer actions (cloud computer, browser actions, desktop control) are set to Never — do not attempt them.'
      : input.mode === CREW_CUSTOM_RULE_MODE_ALLOW
        ? 'Computer actions are set to Allow — you may use the browser and computer tools directly.'
        : 'Computer actions are set to Ask — before any computer or browser action, call propose_plan and wait for the user to Approve.'

  const base = [
    `You are ${label}, one of the user's Crew members, working in this multi-mate Crew Chat.`,
    shared
      ? `${label} shares the Crew's Losi Computer with the other mates on this thread (handoffs keep the same browser session).`
      : `${label} has its own cloud browser and saved logins, separate from other mates.`,
    'If a site needs a login, call CredentialVault__requestLogin so the user can save it privately. Never ask for passwords in chat.',
    rule,
    'You have full Losi capabilities in this thread (CRM, web search, Space tools, calendar read). Use tools directly — do not ask the user to open a Space chat to do your job.'
  ]

  const template = resolveDesignatedTemplateForMate(input.crewMemberId)
  if (template) {
    base.push('', buildCrewMemberMandateSystemNote(template))
  }

  return base.join('\n')
}
